
import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * InvoiceBatch
 *
 * A collection of finalized invoices grouped by billing period, used to manage post-invoicing accounting tasks for a law firm. Tracks trust accounting (IOLTA replenishment status and amounts to transfer from trust to operating), invoice delivery (sent date and method), and is considered complete when a transferDate is recorded.
 */
class InvoiceBatch extends Base implements EntityType {
    invoiceBatchId: uuid;
    startDate?: Date | null;
    endDate: Date;
    transferDate?: Date | null;
    invoiceIds: uuid[];
    invoiceBatchItemIds: uuid[];

    /**
     * Create a new InvoiceBatch, with an (optional) JSON object or InvoiceBatch class
     *
     * @param {Object|InvoiceBatch} doc - Can be either a simple JSON object or a InvoiceBatch class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof InvoiceBatch | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            invoiceBatchId: genUUID(),
            endDate: null,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'invoiceBatch';
        // @ts-ignore
        this.invoiceBatchId = doc._invoiceBatchId || doc.invoiceBatchId || doc.invoicebatchid || genUUID();
        // @ts-ignore
        this.startDate = DateUtil.dateOnly(doc._startDate) || DateUtil.dateOnly(doc.startDate) || DateUtil.dateOnly(doc.startdate) || null;
        // @ts-ignore
        this.endDate = DateUtil.dateOnly(doc._endDate) || DateUtil.dateOnly(doc.endDate) || DateUtil.dateOnly(doc.enddate) || null;
        // @ts-ignore
        this.transferDate = DateUtil.dateOnly(doc._transferDate) || DateUtil.dateOnly(doc.transferDate) || DateUtil.dateOnly(doc.transferdate) || null;
        // @ts-ignore
        this.invoiceIds = doc._invoiceIds || doc.invoiceIds || doc.invoiceids || [];
        // @ts-ignore
        this.invoiceBatchItemIds = doc._invoiceBatchItemIds || doc.invoiceBatchItemIds || doc.invoicebatchitemids || [];

    }
    /**
     * @returns {uuid} This is a generic pointer to the invoiceBatchId across all entity types
     */
    get id(): uuid {
        return this.invoiceBatchId;
    }


    /**
     * Add a invoiceId to invoiceBatch.invoiceIds
     *
     * @param {uuid} invoiceId - The InvoiceId to add
     * @returns uuid - The invoiceId with any defaulted fields set
     */
    addInvoiceId(invoiceId: uuid) : uuid {
        if (!invoiceId) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.invoiceIds) this.invoiceIds = [];
        this.invoiceIds.push(invoiceId);

        return invoiceId;
    }

    /**
     * Update a invoiceId from invoiceBatch
     *
     * @param {uuid} invoiceId - The InvoiceId to remove
     */
    updateInvoiceId(invoiceId: uuid) {
        if (!this.invoiceIds) this.invoiceIds = [];
        
        // Find the matching object by invoiceId.invoiceIdId
        const index = this.invoiceIds.findIndex(item => item === invoiceId);
        if (index !== -1) {
            this.invoiceIds[index] = invoiceId;
        }
        else this.addInvoiceId(invoiceId);
    }

    /**
     * Remove a invoiceId from invoiceBatch
     *
     * @param {uuid} invoiceId - The InvoiceId to remove
     */
    removeInvoiceId(invoiceId: uuid) {
        if (!this.invoiceIds) return;
        
        // Find the matching object by invoiceId.invoiceIdId
        let foundItems = this.invoiceIds?.filter(item => item === invoiceId);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.invoiceIds = this.invoiceIds.filter(item => !(item === invoiceId));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonInvoiceIds() {
        this.invoiceIds = [];
     }
        
    /**
     * Add a invoiceBatchItemId to invoiceBatch.invoiceBatchItemIds
     *
     * @param {uuid} invoiceBatchItemId - The InvoiceBatchItemId to add
     * @returns uuid - The invoiceBatchItemId with any defaulted fields set
     */
    addInvoiceBatchItemId(invoiceBatchItemId: uuid) : uuid {
        if (!invoiceBatchItemId) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.invoiceBatchItemIds) this.invoiceBatchItemIds = [];
        this.invoiceBatchItemIds.push(invoiceBatchItemId);

        return invoiceBatchItemId;
    }

    /**
     * Update a invoiceBatchItemId from invoiceBatch
     *
     * @param {uuid} invoiceBatchItemId - The InvoiceBatchItemId to remove
     */
    updateInvoiceBatchItemId(invoiceBatchItemId: uuid) {
        if (!this.invoiceBatchItemIds) this.invoiceBatchItemIds = [];
        
        // Find the matching object by invoiceBatchItemId.invoiceBatchItemIdId
        const index = this.invoiceBatchItemIds.findIndex(item => item === invoiceBatchItemId);
        if (index !== -1) {
            this.invoiceBatchItemIds[index] = invoiceBatchItemId;
        }
        else this.addInvoiceBatchItemId(invoiceBatchItemId);
    }

    /**
     * Remove a invoiceBatchItemId from invoiceBatch
     *
     * @param {uuid} invoiceBatchItemId - The InvoiceBatchItemId to remove
     */
    removeInvoiceBatchItemId(invoiceBatchItemId: uuid) {
        if (!this.invoiceBatchItemIds) return;
        
        // Find the matching object by invoiceBatchItemId.invoiceBatchItemIdId
        let foundItems = this.invoiceBatchItemIds?.filter(item => item === invoiceBatchItemId);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.invoiceBatchItemIds = this.invoiceBatchItemIds.filter(item => !(item === invoiceBatchItemId));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonInvoiceBatchItemIds() {
        this.invoiceBatchItemIds = [];
     }
        
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this invoiceBatch"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.invoiceBatchId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return ["invoiceIds", "invoiceBatchItemIds"];
    }

    /**
     * Default entries are Outlaw-supplied InvoiceBatch entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): InvoiceBatch[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'invoiceBatch',
            table: 'INVOICE_BATCH',
            comment: 'A collection of finalized invoices grouped by billing period, used to manage post-invoicing accounting tasks for a law firm. Tracks trust accounting (IOLTA replenishment status and amounts to transfer from trust to operating), invoice delivery (sent date and method), and is considered complete when a transferDate is recorded.',
            fields: {
                invoiceBatchId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique id for this invoiceBatch'
                },
                /**
                 * Beginning date for invoice batch (using issueDates).
                 */
                startDate: {
                    type: 'date',
                    comment: 'Start date for activities/expenses included in this invoiceBatch'
                },
                /**
                 * Ending date for invoice batch (using issueDates).
                 */
                endDate: {
                    type: 'date',
                    required: true,
                    comment: 'End date for activities/expenses included in this invoiceBatch'
                },
                /**
                 * Date that money was transferred from trust to operating account.
                 */
                transferDate: {
                    type: 'date',
                    comment: 'Date that the transfer of money was completed'
                },
                /**
                 * Invoice IDs in this batch
                 */
                invoiceIds: {
                    type: 'uuid[]',
                    entityType: 'invoice',
                    comment: 'ID of each invoice included in this batch'
                },
                /**
                 * InvoiceBatchItems in this batch
                 */
                invoiceBatchItemIds: {
                    type: 'uuid[]',
                    entityType: 'invoiceBatchItem',
                    comment: 'Related invoice batch items'
                }
            },
            indexes: [{
                name: 'notDeleted',
                fn: a => a.status !== 'D'
            }],
            maps: [{
                    name: 'invoiceId',
                    keysFn: t => t.invoiceIds,
                    presentFn: (t, k) => t.invoiceIds?.includes(k)
                },
                {
                    name: 'invoiceBatchItemId',
                    keysFn: t => t.invoiceBatchItemIds,
                    presentFn: (t, k) => t.invoiceBatchItemIds?.includes(k)
                },
            ]
        });
    }

}
export default InvoiceBatch;
